<template>
  <div
    :style="style"
    :class="[{
      [classNameActive]: enabled,
      [classNameDragging]: dragging,
      [classNameResizing]: resizing,
      [classNameDraggable]: draggable,
      [classNameResizable]: resizable
    }, className, 'group']"
    @mousedown="elementMouseDown"
    @touchstart="elementTouchDown"
  >
    <slot />
    <template v-if="resizable">
      <div
        v-for="point in actualHandles"
        :key="point"
        :class="[classNameHandle, classNameHandle + '-' + point, 'z-50 opacity-0 group-hover:opacity-100 transition-all duration-200 ease-in-out p-1 text-gray-700']"
        @mousedown.stop.prevent="handleDown(point, $event)"
        @touchstart.stop.prevent="handleTouchDown(point, $event)"
      >
        <o-icon icon="resize" :size="11" />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
export default { name: 'ODraggable' }
</script>

<script setup lang="ts">
const eventsConfig = {
  mouse: { start: 'mousedown', move: 'mousemove', stop: 'mouseup' },
  touch: { start: 'touchstart', move: 'touchmove', stop: 'touchend' },
}
const userSelectNone: Record<string, string> = { userSelect: 'none', MozUserSelect: 'none', WebkitUserSelect: 'none', MsUserSelect: 'none' }
const userSelectAuto: Record<string, string> = { userSelect: 'auto', MozUserSelect: 'auto', WebkitUserSelect: 'auto', MsUserSelect: 'auto' }
let eventsFor = eventsConfig.mouse

const instance = getCurrentInstance()

interface Props {
  className?: string
  classNameDraggable?: string
  classNameResizable?: string
  classNameDragging?: string
  classNameResizing?: string
  classNameActive?: string
  classNameHandle?: string
  disableUserSelect?: boolean
  enableNativeDrag?: boolean
  preventDeactivation?: boolean
  active?: boolean
  draggable?: boolean
  resizable?: boolean
  lockAspectRatio?: boolean
  w?: number | string
  h?: number | string
  minWidth?: number
  minHeight?: number
  maxWidth?: number | null
  maxHeight?: number | null
  widthFormat?: string
  heightFormat?: string
  x?: number
  y?: number
  z?: string | number
  handles?: string[]
  dragHandle?: string | null
  dragCancel?: string | null
  axis?: string
  grid?: number[]
  parent?: boolean | string
  scale?: number
  onDragStart?: Function
  onDrag?: Function
  onResizeStart?: Function
  onResize?: Function
  widthLimit?: number
}

const props = withDefaults(defineProps<Props>(), {
  className: 'o-draggable',
  classNameDraggable: 'draggable',
  classNameResizable: 'resizable',
  classNameDragging: 'dragging',
  classNameResizing: 'resizing',
  classNameActive: 'active',
  classNameHandle: 'handle',
  disableUserSelect: true,
  enableNativeDrag: false,
  preventDeactivation: false,
  active: false,
  draggable: true,
  resizable: true,
  lockAspectRatio: false,
  w: 200,
  h: 200,
  minWidth: 0,
  minHeight: 0,
  maxWidth: null,
  maxHeight: null,
  widthFormat: 'px',
  heightFormat: 'px',
  x: 0,
  y: 0,
  z: 20,
  handles: () => ['tl', 'tm', 'tr', 'mr', 'br', 'bm', 'bl', 'ml'],
  dragHandle: null,
  dragCancel: null,
  axis: 'both',
  grid: () => [1, 1],
  parent: false,
  scale: 1,
  onDragStart: () => () => true,
  onDrag: () => () => true,
  onResizeStart: () => () => true,
  onResize: () => () => true,
  widthLimit: 0,
})

const emit = defineEmits<{
  (e: 'activated'): void
  (e: 'deactivated'): void
  (e: 'update:active', value: boolean): void
  (e: 'dragging', left: number, top: number): void
  (e: 'dragstop', left: number, top: number): void
  (e: 'resizing', left: number, top: number, width: number, height: number): void
  (e: 'resizestop', left: number, top: number, width: number, height: number): void
}>()

const left = ref(props.x)
const top = ref(props.y)
const right = ref<number | null>(null)
const bottom = ref<number | null>(null)
const width = ref<number | null>(null)
const height = ref<number | null>(null)
const widthTouched = ref(false)
const heightTouched = ref(false)
const aspectFactor = ref<number | null>(null)
const parentWidth = ref<number | null>(null)
const parentHeight = ref<number | null>(null)
const minW = ref(props.minWidth)
const minH = ref(props.minHeight)
const maxW = ref(props.maxWidth)
const maxH = ref(props.maxHeight)
const handle = ref<string | null>(null)
const enabled = ref(props.active)
const resizing = ref(false)
const dragging = ref(false)
const zIndex = ref(props.z)
const bounds = ref<any>(null)
let mouseClickPosition = { mouseX: 0, mouseY: 0, x: 0, y: 0, w: 0, h: 0, left: 0, right: 0, top: 0, bottom: 0 }

const $el = computed(() => instance?.proxy?.$el as HTMLElement)

const style = computed(() => ({
  top: top.value + 'px',
  left: left.value + 'px',
  width: computedWidth.value,
  height: computedHeight.value,
  ...(dragging.value && props.disableUserSelect ? userSelectNone : userSelectAuto),
}))

const actualHandles = computed(() => {
  if (!props.resizable) { return [] }
  return props.handles
})

const computedWidth = computed(() => {
  if (props.w === 'auto') {
    if (!widthTouched.value) { return 'auto' }
  }
  return width.value + props.widthFormat
})

const computedHeight = computed(() => {
  if (props.h === 'auto') {
    if (!heightTouched.value) { return 'auto' }
  }
  return height.value + props.heightFormat
})

const resizingOnX = computed(() => Boolean(handle.value) && (handle.value!.includes('l') || handle.value!.includes('r')))
const resizingOnY = computed(() => Boolean(handle.value) && (handle.value!.includes('t') || handle.value!.includes('b')))
const isCornerHandle = computed(() => Boolean(handle.value) && ['tl', 'tr', 'br', 'bl'].includes(handle.value!))

function resetBoundsAndMouseState () {
  mouseClickPosition = { mouseX: 0, mouseY: 0, x: 0, y: 0, w: 0, h: 0, left: 0, right: 0, top: 0, bottom: 0 }
  bounds.value = { minLeft: null, maxLeft: null, minRight: null, maxRight: null, minTop: null, maxTop: null, minBottom: null, maxBottom: null }
}

function isFunction (func: any) {
  return (typeof func === 'function' || Object.prototype.toString.call(func) === '[object Function]')
}

function addEvent (el: any, event: string, handler: any) {
  if (!el) { return }
  if (el.attachEvent) { el.attachEvent('on' + event, handler) }
  else if (el.addEventListener) { el.addEventListener(event, handler, true) }
  else { el['on' + event] = handler }
}

function removeEvent (el: any, event: string, handler: any) {
  if (!el) { return }
  if (el.detachEvent) { el.detachEvent('on' + event, handler) }
  else if (el.removeEventListener) { el.removeEventListener(event, handler, true) }
  else { el['on' + event] = null }
}

function snapToGrid (grid: number[], pendingX: number, pendingY: number, scale = 1) {
  const x = Math.round((pendingX / scale) / grid[0]) * grid[0]
  const y = Math.round((pendingY / scale) / grid[1]) * grid[1]
  return [x, y]
}

function restrictToBounds (value: number, min: number | null, max: number | null) {
  if (min !== null && value < min) { return min }
  if (max !== null && max < value) { return max }
  return value
}

function computeWidthFn (pw: number, l: number, r: number) { return pw - l - r }
function computeHeightFn (ph: number, t: number, b: number) { return ph - t - b }

function getComputedSizeFn (el: HTMLElement) {
  const s = window.getComputedStyle(el)
  return [parseFloat(s.getPropertyValue('width')), parseFloat(s.getPropertyValue('height'))]
}

function getParentSize (): [number | null, number | null] {
  if (props.parent) {
    const parentEl = typeof props.parent === 'string' ? document.getElementById(props.parent) : $el.value?.parentNode as HTMLElement
    const s = window.getComputedStyle(parentEl!, null)
    return [
      props.widthLimit || parseInt(s.getPropertyValue('width'), 10),
      parseInt(s.getPropertyValue('height'), 10),
    ]
  }
  return [null, null]
}

function checkParentSize () {
  if (props.parent) {
    const [pw, ph] = getParentSize()
    parentWidth.value = pw
    parentHeight.value = ph
  }
}

function matchesSelectorToParentElements (el: any, selector: string, baseNode: HTMLElement) {
  let node = el
  const matchesSelectorFunc = ['matches', 'webkitMatchesSelector', 'mozMatchesSelector', 'msMatchesSelector', 'oMatchesSelector'].find(func => isFunction(node[func]))
  if (!matchesSelectorFunc || !isFunction(node[matchesSelectorFunc])) { return false }
  do {
    if (node[matchesSelectorFunc](selector)) { return true }
    if (node === baseNode) { return false }
    node = node.parentNode
  } while (node)
  return false
}

function calcDragLimits () {
  return {
    minLeft: left.value % props.grid[0],
    maxLeft: Math.floor((parentWidth.value! - width.value! - left.value) / props.grid[0]) * props.grid[0] + left.value,
    minRight: right.value! % props.grid[0],
    maxRight: Math.floor((parentWidth.value! - width.value! - right.value!) / props.grid[0]) * props.grid[0] + right.value!,
    minTop: top.value % props.grid[1],
    maxTop: Math.floor((parentHeight.value! - height.value! - top.value) / props.grid[1]) * props.grid[1] + top.value,
    minBottom: bottom.value! % props.grid[1],
    maxBottom: Math.floor((parentHeight.value! - height.value! - bottom.value!) / props.grid[1]) * props.grid[1] + bottom.value!,
  }
}

function calcResizeLimits () {
  let mW = minW.value
  let mH = minH.value
  let mxW = maxW.value || 0
  let mxH = maxH.value || 0
  const af = aspectFactor.value!
  const [gridX, gridY] = props.grid
  const w = width.value!
  const h = height.value!
  const l = left.value
  const t = top.value
  const r = right.value!
  const b = bottom.value!
  if (props.lockAspectRatio) {
    if (mW / mH > af) { mH = mW / af } else { mW = af * mH }
    if (mxW && mxH) { mxW = Math.min(mxW, af * mxH); mxH = Math.min(mxH, mxW / af) }
    else if (mxW) { mxH = mxW / af }
    else if (mxH) { mxW = af * mxH }
  }
  mxW = mxW - (mxW % gridX)
  mxH = mxH - (mxH % gridY)
  const limits: any = { minLeft: null, maxLeft: null, minTop: null, maxTop: null, minRight: null, maxRight: null, minBottom: null, maxBottom: null }
  if (props.parent) {
    limits.minLeft = l % gridX; limits.maxLeft = l + Math.floor((w - mW) / gridX) * gridX
    limits.minTop = t % gridY; limits.maxTop = t + Math.floor((h - mH) / gridY) * gridY
    limits.minRight = r % gridX; limits.maxRight = r + Math.floor((w - mW) / gridX) * gridX
    limits.minBottom = b % gridY; limits.maxBottom = b + Math.floor((h - mH) / gridY) * gridY
    if (mxW) { limits.minLeft = Math.max(limits.minLeft, parentWidth.value! - r - mxW); limits.minRight = Math.max(limits.minRight, parentWidth.value! - l - mxW) }
    if (mxH) { limits.minTop = Math.max(limits.minTop, parentHeight.value! - b - mxH); limits.minBottom = Math.max(limits.minBottom, parentHeight.value! - t - mxH) }
    if (props.lockAspectRatio) {
      limits.minLeft = Math.max(limits.minLeft, l - t * af); limits.minTop = Math.max(limits.minTop, t - l / af)
      limits.minRight = Math.max(limits.minRight, r - b * af); limits.minBottom = Math.max(limits.minBottom, b - r / af)
    }
  } else {
    limits.maxLeft = l + Math.floor((w - mW) / gridX) * gridX; limits.maxTop = t + Math.floor((h - mH) / gridY) * gridY
    limits.maxRight = r + Math.floor((w - mW) / gridX) * gridX; limits.maxBottom = b + Math.floor((h - mH) / gridY) * gridY
    if (mxW) { limits.minLeft = -(r + mxW); limits.minRight = -(l + mxW) }
    if (mxH) { limits.minTop = -(b + mxH); limits.minBottom = -(t + mxH) }
    if (props.lockAspectRatio && (mxW && mxH)) {
      limits.minLeft = Math.min(limits.minLeft, -(r + mxW)); limits.minTop = Math.min(limits.minTop, -(mxH + b))
      limits.minRight = Math.min(limits.minRight, -l - mxW); limits.minBottom = Math.min(limits.minBottom, -t - mxH)
    }
  }
  return limits
}

function handleDrag (e: any) {
  const ax = props.axis
  const grid = props.grid
  const b = bounds.value
  const mcp = mouseClickPosition
  const tmpDeltaX = ax && ax !== 'y' ? mcp.mouseX - (e.touches ? e.touches[0].pageX : e.pageX) : 0
  const tmpDeltaY = ax && ax !== 'x' ? mcp.mouseY - (e.touches ? e.touches[0].pageY : e.pageY) : 0
  const [deltaX, deltaY] = snapToGrid(grid, tmpDeltaX, tmpDeltaY, props.scale)
  const l = restrictToBounds(mcp.left - deltaX, b.minLeft, b.maxLeft)
  const t = restrictToBounds(mcp.top - deltaY, b.minTop, b.maxTop)
  if (props.onDrag!(l, t) === false) { return }
  const r = restrictToBounds(mcp.right + deltaX, b.minRight, b.maxRight)
  const bo = restrictToBounds(mcp.bottom + deltaY, b.minBottom, b.maxBottom)
  left.value = l; top.value = t; right.value = r; bottom.value = bo
  emit('dragging', left.value, top.value)
}

function handleResizeFn (e: any) {
  let l = left.value
  let t = top.value
  let r = right.value!
  let b = bottom.value!
  const mcp = mouseClickPosition
  const af = aspectFactor.value!
  const tmpDeltaX = mcp.mouseX - (e.touches ? e.touches[0].pageX : e.pageX)
  const tmpDeltaY = mcp.mouseY - (e.touches ? e.touches[0].pageY : e.pageY)
  if (!widthTouched.value && tmpDeltaX) { widthTouched.value = true }
  if (!heightTouched.value && tmpDeltaY) { heightTouched.value = true }
  const [deltaX, deltaY] = snapToGrid(props.grid, tmpDeltaX, tmpDeltaY, props.scale)
  if (handle.value!.includes('b')) {
    b = restrictToBounds(mcp.bottom + deltaY, bounds.value.minBottom, bounds.value.maxBottom)
    if (props.lockAspectRatio && resizingOnY.value) { r = right.value! - (bottom.value! - b) * af }
  } else if (handle.value!.includes('t')) {
    t = restrictToBounds(mcp.top - deltaY, bounds.value.minTop, bounds.value.maxTop)
    if (props.lockAspectRatio && resizingOnY.value) { l = left.value - (top.value - t) * af }
  }
  if (handle.value!.includes('r')) {
    r = restrictToBounds(mcp.right + deltaX, bounds.value.minRight, bounds.value.maxRight)
    if (props.lockAspectRatio && resizingOnX.value) { b = bottom.value! - (right.value! - r) / af }
  } else if (handle.value!.includes('l')) {
    l = restrictToBounds(mcp.left - deltaX, bounds.value.minLeft, bounds.value.maxLeft)
    if (props.lockAspectRatio && resizingOnX.value) { t = top.value - (left.value - l) / af }
  }
  const w = computeWidthFn(parentWidth.value!, l, r)
  const h = computeHeightFn(parentHeight.value!, t, b)
  if (props.onResize!(handle.value, l, t, w, h) === false) { return }
  left.value = l; top.value = t; right.value = r; bottom.value = b; width.value = w; height.value = h
  emit('resizing', left.value, top.value, width.value!, height.value!)
}

function move (e: any) {
  if (resizing.value) { handleResizeFn(e) }
  else if (dragging.value) { handleDrag(e) }
}

function handleUp (e: any) {
  handle.value = null
  resetBoundsAndMouseState()
  if (resizing.value) { resizing.value = false; emit('resizestop', left.value, top.value, width.value!, height.value!) }
  if (dragging.value) { dragging.value = false; emit('dragstop', left.value, top.value) }
  removeEvent(document.documentElement, eventsFor.move, handleResizeFn)
}

function deselect (e: any) {
  const target = e.target || e.srcElement
  const regex = new RegExp(props.className + '-([trmbl]{2})', '')
  if (!$el.value?.contains(target) && !regex.test(target.className)) {
    if (enabled.value && !props.preventDeactivation) {
      enabled.value = false; emit('deactivated'); emit('update:active', false)
    }
    removeEvent(document.documentElement, eventsFor.move, handleResizeFn)
  }
  resetBoundsAndMouseState()
}

function elementDown (e: any) {
  if (e instanceof MouseEvent && e.which !== 1) { return }
  const target = e.target || e.srcElement
  if ($el.value?.contains(target)) {
    if (props.onDragStart!(e) === false) { return }
    if (
      (props.dragHandle && !matchesSelectorToParentElements(target, props.dragHandle, $el.value!)) ||
      (props.dragCancel && matchesSelectorToParentElements(target, props.dragCancel, $el.value!))
    ) { dragging.value = false; return }
    if (!enabled.value) { enabled.value = true; emit('activated'); emit('update:active', true) }
    if (props.draggable) { dragging.value = true }
    mouseClickPosition.mouseX = e.touches ? e.touches[0].pageX : e.pageX
    mouseClickPosition.mouseY = e.touches ? e.touches[0].pageY : e.pageY
    mouseClickPosition.left = left.value; mouseClickPosition.right = right.value!
    mouseClickPosition.top = top.value; mouseClickPosition.bottom = bottom.value!
    if (props.parent) { bounds.value = calcDragLimits() }
    addEvent(document.documentElement, eventsFor.move, move)
    addEvent(document.documentElement, eventsFor.stop, handleUp)
  }
}

function elementTouchDown (e: any) { eventsFor = eventsConfig.touch; elementDown(e) }
function elementMouseDown (e: any) { eventsFor = eventsConfig.mouse; elementDown(e) }

function handleTouchDown (h: string, e: any) { eventsFor = eventsConfig.touch; handleDown(h, e) }

function handleDown (h: string, e: any) {
  if (e instanceof MouseEvent && e.which !== 1) { return }
  if (props.onResizeStart!(h, e) === false) { return }
  if (e.stopPropagation) { e.stopPropagation() }
  if (props.lockAspectRatio && !h.includes('m')) { handle.value = 'm' + h.substring(1) } else { handle.value = h }
  resizing.value = true
  mouseClickPosition.mouseX = e.touches ? e.touches[0].pageX : e.pageX
  mouseClickPosition.mouseY = e.touches ? e.touches[0].pageY : e.pageY
  mouseClickPosition.left = left.value; mouseClickPosition.right = right.value!
  mouseClickPosition.top = top.value; mouseClickPosition.bottom = bottom.value!
  bounds.value = calcResizeLimits()
  addEvent(document.documentElement, eventsFor.move, handleResizeFn)
  addEvent(document.documentElement, eventsFor.stop, handleUp)
}

function moveHorizontally (val: number) {
  const [deltaX] = snapToGrid(props.grid, val, top.value, props.scale)
  const l = restrictToBounds(deltaX, bounds.value.minLeft, bounds.value.maxLeft)
  left.value = l; right.value = parentWidth.value! - width.value! - l
}

function moveVertically (val: number) {
  const [, deltaY] = snapToGrid(props.grid, left.value, val, props.scale)
  const t = restrictToBounds(deltaY, bounds.value.minTop, bounds.value.maxTop)
  top.value = t; bottom.value = parentHeight.value! - height.value! - t
}

function changeWidth (val: number) {
  const [newWidth] = snapToGrid(props.grid, val, 0, props.scale)
  const r = restrictToBounds(parentWidth.value! - newWidth - left.value, bounds.value.minRight, bounds.value.maxRight)
  let b = bottom.value!
  if (props.lockAspectRatio) { b = bottom.value! - (right.value! - r) / aspectFactor.value! }
  width.value = computeWidthFn(parentWidth.value!, left.value, r)
  height.value = computeHeightFn(parentHeight.value!, top.value, b)
  right.value = r; bottom.value = b
}

function changeHeight (val: number) {
  const [, newHeight] = snapToGrid(props.grid, 0, val, props.scale)
  const b = restrictToBounds(parentHeight.value! - newHeight - top.value, bounds.value.minBottom, bounds.value.maxBottom)
  let r = right.value!
  if (props.lockAspectRatio) { r = right.value! - (bottom.value! - b) * aspectFactor.value! }
  width.value = computeWidthFn(parentWidth.value!, left.value, r)
  height.value = computeHeightFn(parentHeight.value!, top.value, b)
  right.value = r; bottom.value = b
}

// Watchers
watch(() => props.active, (val) => {
  enabled.value = val
  if (val) { emit('activated') } else { emit('deactivated') }
})
watch(() => props.z, (val) => { if (val >= 0 || val === 'auto') { zIndex.value = val } })
watch(() => props.x, (val) => {
  if (resizing.value || dragging.value) { return }
  if (props.parent) { bounds.value = calcDragLimits() }
  moveHorizontally(val)
})
watch(() => props.y, (val) => {
  if (resizing.value || dragging.value) { return }
  if (props.parent) { bounds.value = calcDragLimits() }
  moveVertically(val)
})
watch(() => props.lockAspectRatio, (val) => {
  if (val) { aspectFactor.value = width.value! / height.value! } else { aspectFactor.value = null }
})
watch(() => props.minWidth, (val) => { if (val > 0 && val <= width.value!) { minW.value = val } })
watch(() => props.minHeight, (val) => { if (val > 0 && val <= height.value!) { minH.value = val } })
watch(() => props.maxWidth, (val) => { maxW.value = val })
watch(() => props.maxHeight, (val) => { maxH.value = val })
watch(() => props.w, (val) => {
  if (resizing.value || dragging.value) { return }
  if (props.parent) { bounds.value = calcResizeLimits() }
  changeWidth(val as number)
})
watch(() => props.h, (val) => {
  if (resizing.value || dragging.value) { return }
  if (props.parent) { bounds.value = calcResizeLimits() }
  changeHeight(val as number)
})

// Lifecycle
resetBoundsAndMouseState()

onMounted(() => {
  if (!props.enableNativeDrag) { $el.value!.ondragstart = () => false }
  const [pw, ph] = getParentSize()
  parentWidth.value = pw; parentHeight.value = ph
  const [cw, ch] = getComputedSizeFn($el.value!)
  aspectFactor.value = (props.w !== 'auto' ? props.w as number : cw) / (props.h !== 'auto' ? props.h as number : ch)
  width.value = props.w !== 'auto' ? props.w as number : cw
  height.value = props.h !== 'auto' ? props.h as number : ch
  right.value = parentWidth.value! - width.value - left.value
  bottom.value = parentHeight.value! - height.value - top.value
  addEvent(document.documentElement, 'mousedown', deselect)
  addEvent(document.documentElement, 'touchend touchcancel', deselect)
  addEvent(window, 'resize', checkParentSize)
})

onBeforeUnmount(() => {
  removeEvent(document.documentElement, 'mousedown', deselect)
  removeEvent(document.documentElement, 'touchstart', handleUp)
  removeEvent(document.documentElement, 'mousemove', move)
  removeEvent(document.documentElement, 'touchmove', move)
  removeEvent(document.documentElement, 'mouseup', handleUp)
  removeEvent(document.documentElement, 'touchend touchcancel', deselect)
  removeEvent(window, 'resize', checkParentSize)
})
</script>

<style lang="scss">
.o-draggable {
  touch-action: none;
  position: absolute;
  box-sizing: border-box;
  top: 0
}
.handle {
  box-sizing: border-box;
  position: absolute;
  width: 10px;
  height: 10px;
}
.handle-tl {
  top: -10px;
  left: -10px;
  cursor: nw-resize;
}
.handle-tm {
  top: -10px;
  left: 50%;
  margin-left: -5px;
  cursor: n-resize;
}
.handle-tr {
  top: -10px;
  right: -10px;
  cursor: ne-resize;
}
.handle-ml {
  top: 50%;
  margin-top: -5px;
  left: -10px;
  cursor: w-resize;
}
.handle-mr {
  top: 50%;
  margin-top: -5px;
  right: -10px;
  cursor: e-resize;
}
.handle-bl {
  bottom: -10px;
  left: -10px;
  cursor: sw-resize;
}
.handle-bm {
  bottom: 8px;
  left: 50%;
  margin-left: -5px;
  cursor: s-resize;
}
.handle-br {
  bottom: -10px;
  right: -10px;
  cursor: se-resize;
}
@media only screen and (max-width: 768px) {
  [class*="handle-"]:before {
    content: '';
    left: -10px;
    right: -10px;
    bottom: -10px;
    top: -10px;
    position: absolute;
  }
}
</style>
